import { Separator } from "@attmos/flowstack-ui";
import * as React from "react";

import { EmptyState } from "@/components/layout/EmptyState";
import { ParameterField, type ParameterShape } from "@/components/submission/ParameterField";

/**
 * The parameters a submitter can actually set: ``source: user`` (which
 * is what an unstated source means) and not hidden by
 * ``ui_visible: false``.
 *
 * Exported because a host page needs the SAME predicate to seed its
 * values dict with the declared defaults — the flow submit page does
 * exactly that (#831). Restating the filter there would let the form's
 * idea of "settable" and the request body's idea of it drift apart,
 * which is how a knob ends up rendered but never submitted.
 */
export function userSettableParameters(
  manifest: Record<string, unknown> | undefined
): Array<[string, ParameterShape]> {
  const params = (manifest?.parameters ?? {}) as Record<string, ParameterShape>;
  return Object.entries(params).filter(
    ([, p]) => (p.source ?? "user") === "user" && p.ui_visible !== false
  );
}

/**
 * Declared defaults for every settable parameter, keyed by name.
 *
 * A parameter with no declared default is **omitted**, and the null case
 * is load-bearing rather than tidiness: a manifest is a `model_dump`, so
 * "no default" arrives as an explicit ``default: null``, not as a missing
 * key. Sending that null back would not be a no-op —
 * ``compile_flow`` resolves a flow parameter with
 *
 *     if pname in parameters:      -> take the caller's value
 *     elif pspec.default is not None: -> take the declared default
 *                                  -> (otherwise: leave it UNRESOLVED)
 *
 * [ev: ``flow/dag.py:1135-1139``]. A submitted null therefore lands in
 * the first branch and makes the name *present* with value ``None``,
 * which suppresses the ``MissingFlowParameterError`` a node binding
 * ``$.parameters.X`` is supposed to raise and puts a ``None`` into the
 * context ``when:`` predicates are evaluated against. Omitting it leaves
 * the compiler's own three-way resolution intact.
 */
export function declaredParameterDefaults(
  manifest: Record<string, unknown> | undefined
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [name, param] of userSettableParameters(manifest)) {
    if (param.default !== undefined && param.default !== null) out[name] = param.default;
  }
  return out;
}

/**
 * The subset of a host page's ``edits`` that the CURRENT manifest can
 * still render — i.e. exactly what the form is displaying.
 *
 * A host page holds the user's edits across manifest refetches (they are
 * cleared only when the subject changes), so a template redeployed under
 * an open page can leave behind an edit that no longer fits its
 * declaration: the parameter may be gone or newly ``ui_visible: false``
 * (Rupert PR-841 r1), or still settable but re-typed with different enum
 * members (r2). A value the control cannot show must not be submitted
 * either — ``ManifestForm`` falls back to ``param.default`` whenever an
 * edit is absent, so dropping the edit is precisely what keeps the
 * request body saying what the user is looking at.
 *
 * The ``null`` case is the reachable one, and it is not about staleness:
 * every numeric control emits ``null`` for an emptied field while
 * continuing to DISPLAY the declared default, so keeping it would submit
 * an explicit null for a knob reading ``20`` — and a submitted null is
 * not a no-op, it suppresses ``compile_flow``'s own default resolution
 * (see ``declaredParameterDefaults`` above).
 */
export function renderableParameterEdits(
  manifest: Record<string, unknown> | undefined,
  edits: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [name, param] of userSettableParameters(manifest)) {
    if (!(name in edits)) continue;
    if (isRenderableValue(param, edits[name])) out[name] = edits[name];
  }
  return out;
}

/** Would ``ParameterField`` show this value under this declaration? */
function isRenderableValue(param: ParameterShape, value: unknown): boolean {
  if (value === null || value === undefined) return false;
  switch (param.type) {
    case "enum":
      // The select matches its options by ``String(v)`` and re-emits the
      // original typed member, so a value no longer among them renders
      // as no selection at all.
      return (param.values ?? []).some((v) => String(v) === String(value));
    case "int":
    case "float":
      return typeof value === "number" && Number.isFinite(value);
    case "bool":
      return typeof value === "boolean";
    case "string":
      return typeof value === "string";
    case "quantity":
      return (
        typeof value === "object" &&
        "val" in (value as object) &&
        typeof (value as { val: unknown }).val === "number"
      );
    default:
      // A type ``renderControl`` has no case for gets no control at all.
      // The manifest is read defensively, so this is reachable at runtime
      // even though the declared union says otherwise.
      return false;
  }
}

/**
 * Manifest-driven form generator. Reads the raw ``TileManifest``
 * dump (Record<string, unknown>) and emits one ParameterField per
 * ``source == 'user'`` && ``ui_visible != false`` parameter, plus
 * read-only chips for ``fanout`` / ``inherited`` parameters so users
 * can see what the platform is injecting.
 *
 * Also drives the **Flow** submit page (#831): a ``FlowParameter`` is
 * the same shape minus ``source``/``required``, so the identical
 * filtering, defaulting and expert-collapse apply. Only the empty-state
 * wording differs, which ``subject`` supplies.
 *
 * Expert-tagged fields collapse behind a "Show expert parameters"
 * toggle so the default view stays uncluttered.
 *
 * Values are uncontrolled at the manifest level — the host page owns
 * the values dict and threads in onChange. This keeps the cost-preview
 * panel reactive to every keystroke without re-rendering the whole
 * form on each parameter change.
 */
export interface ManifestFormProps {
  manifest: Record<string, unknown> | undefined;
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  isLoading?: boolean;
  /** What the manifest describes — empty-state copy only. The Flow
   * submit page must not tell a user to "Pick a Tile first". */
  subject?: "Tile" | "Flow";
}

export function ManifestForm({
  manifest,
  values,
  onChange,
  isLoading,
  subject = "Tile",
}: ManifestFormProps): JSX.Element {
  const [showExpert, setShowExpert] = React.useState(false);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <div className="h-9 rounded-md bg-surface-2 motion-safe:animate-pulse" />
        <div className="h-9 rounded-md bg-surface-2 motion-safe:animate-pulse" />
        <div className="h-9 rounded-md bg-surface-2 motion-safe:animate-pulse" />
      </div>
    );
  }

  if (!manifest) {
    return (
      <EmptyState
        title={`Pick a ${subject} first.`}
        description={`Once a ${subject} is selected, its declared parameters appear here.`}
      />
    );
  }

  const params = (manifest.parameters ?? {}) as Record<string, ParameterShape>;
  const entries = Object.entries(params);

  if (entries.length === 0) {
    return (
      <EmptyState
        title={`This ${subject} takes no parameters.`}
        description="The submit button uses the manifest defaults verbatim."
      />
    );
  }

  const userParams = userSettableParameters(manifest);
  const platformParams = entries.filter(
    ([, p]) => p.source && p.source !== "user" && p.ui_visible !== false
  );

  const normalUser = userParams.filter(([, p]) => !p.expert);
  const expertUser = userParams.filter(([, p]) => p.expert);

  const updateValue = (name: string, next: unknown): void => {
    onChange({ ...values, [name]: next });
  };

  return (
    <div className="flex flex-col gap-4">
      {normalUser.length > 0 && (
        <section className="flex flex-col gap-3">
          {normalUser.map(([name, param]) => (
            <ParameterField
              key={name}
              name={name}
              parameter={param}
              value={values[name] ?? param.default}
              onChange={(v) => updateValue(name, v)}
            />
          ))}
        </section>
      )}

      {platformParams.length > 0 && (
        <>
          <Separator />
          <section className="flex flex-col gap-3" aria-labelledby="platform-params-heading">
            <h4 id="platform-params-heading" className="label-micro text-muted-foreground">
              Platform-injected
            </h4>
            {platformParams.map(([name, param]) => (
              <ParameterField
                key={name}
                name={name}
                parameter={param}
                value={values[name] ?? param.default}
                onChange={() => {
                  /* read-only — onChange wired but never fires */
                }}
                readOnly
              />
            ))}
          </section>
        </>
      )}

      {expertUser.length > 0 && (
        <>
          <Separator />
          <section className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => setShowExpert((v) => !v)}
              className="self-start text-xs font-medium text-cyan hover:underline"
              aria-expanded={showExpert}
            >
              {showExpert ? "Hide" : "Show"} expert parameters ({expertUser.length})
            </button>
            {showExpert &&
              expertUser.map(([name, param]) => (
                <ParameterField
                  key={name}
                  name={name}
                  parameter={param}
                  value={values[name] ?? param.default}
                  onChange={(v) => updateValue(name, v)}
                />
              ))}
          </section>
        </>
      )}
    </div>
  );
}
